export interface RunwayDepthCue {
  expectedAltitudeMeters: number;
  glideErrorMeters: number;
  planeVerticalOffset: number;
  altitudeLift: number;
  shadowAlpha: number;
  shadowScale: number;
  shadowSeparation: number;
  status: "high" | "on-path" | "low";
}

export interface RunwayPerspectiveCue {
  nearRunwayVisibility: number;
}

const GLIDE_DEGREES = 3.5;
const REFERENCE_ALTITUDE_METERS = 73;

/** Maps flight telemetry to normalized screen-space cues shared by both TV renderers. */
export function runwayDepthCue(
  distanceToThresholdMeters: number,
  altitudeMeters: number,
): RunwayDepthCue {
  const safeDistance = Math.max(0, finiteOrZero(distanceToThresholdMeters));
  const safeAltitude = Math.max(0, finiteOrZero(altitudeMeters));
  const expectedAltitudeMeters = safeDistance * Math.tan((GLIDE_DEGREES * Math.PI) / 180);
  const glideErrorMeters = safeAltitude - expectedAltitudeMeters;
  const heightRatio = clamp(safeAltitude / REFERENCE_ALTITUDE_METERS, 0, 1);
  const perceivedHeight = Math.sqrt(heightRatio);

  return {
    expectedAltitudeMeters,
    glideErrorMeters,
    planeVerticalOffset: clamp(glideErrorMeters / 55, -0.12, 0.12),
    altitudeLift: safeAltitude <= 0.05 ? 0 : 0.04 + perceivedHeight * 0.18,
    shadowAlpha: 0.4 - perceivedHeight * 0.2,
    shadowScale: 1 - perceivedHeight * 0.38,
    shadowSeparation: safeAltitude <= 0.05 ? 0 : 0.025 + perceivedHeight * 0.16,
    status: glideErrorMeters > 7 ? "high" : glideErrorMeters < -7 ? "low" : "on-path",
  };
}

/** Reveals the runway from the horizon as the aircraft joins final approach. */
export function runwayPerspectiveCue(distanceToThresholdMeters: number): RunwayPerspectiveCue {
  const safeDistance = Math.max(0, finiteOrZero(distanceToThresholdMeters));
  return {
    nearRunwayVisibility: clamp((1_100 - safeDistance) / 750, 0, 1),
  };
}

function finiteOrZero(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
